import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export interface AssistResponse {
  text: string;
  model: string;
  feature: string;
  ok: boolean;
}

export interface ScribeRow {
  medicineName: string;
  dosageCode: string;
  days: string;
  daysUnit: string;
  site: string;
  qty: number | null;
  remarks: string;
}

export interface ScribeResponse {
  rows: ScribeRow[];
  model: string;
  ok: boolean;
  section: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class AiAssistService {
  private readonly URL = `${environment.chatbotApiUrl}assist`;

  constructor(private http: HttpClient) {}

  generate(feature: string, context: string, language = 'en'): Observable<AssistResponse> {
    return this.http.post<AssistResponse>(this.URL, { feature, context, language });
  }

  /** transcript -> structured EMR rows for the given section (e.g. 'medicine') */
  scribe(section: string, transcript: string): Observable<ScribeResponse> {
    return this.http.post<ScribeResponse>(
      `${environment.chatbotApiUrl}scribe`,
      { section, transcript },
    );
  }

  /** transcript -> the ENTIRE doctor workup (all sections) */
  scribeWorkup(transcript: string): Observable<WorkupResponse> {
    return this.http.post<WorkupResponse>(
      `${environment.chatbotApiUrl}scribe/workup`,
      { transcript },
    );
  }

  /** audio blob -> transcript via the server-side Sarvam STT proxy */
  scribeTranscribe(audio: Blob, filename = 'audio.wav'): Observable<TranscribeResponse> {
    const form = new FormData();
    form.append('file', audio, filename);
    form.append('language_code', 'unknown'); // auto-detect (handles code-switching)
    return this.http.post<TranscribeResponse>(
      `${environment.chatbotApiUrl}scribe/transcribe`,
      form,
    );
  }

  /** stock list -> AI expiry / reorder / dead-stock insights */
  inventoryInsights(items: any[], asOfDate: string): Observable<InventoryInsights> {
    return this.http.post<InventoryInsights>(
      `${environment.chatbotApiUrl}inventory/insights`,
      { items, asOfDate },
    );
  }

  /** live: read real stock from the WebNetram DB, then analyse */
  inventoryInsightsLive(asOfDate: string, branchId?: number | null): Observable<InventoryInsights> {
    return this.http.post<InventoryInsights>(
      `${environment.chatbotApiUrl}inventory/insights-live`,
      { asOfDate, branchId: branchId == null ? null : branchId },
    );
  }

  /** branch list for the per-branch filter */
  inventoryBranches(): Observable<{ branches: { id: number; name: string }[] }> {
    return this.http.get<{ branches: { id: number; name: string }[] }>(
      `${environment.chatbotApiUrl}inventory/branches`,
    );
  }

  /** Store cockpit: catalog of available metric tiles */
  storeCatalog(): Observable<{ metrics: any[] }> {
    return this.http.get<{ metrics: any[] }>(`${environment.chatbotApiUrl}store/catalog`);
  }

  /** Store cockpit: chart-ready data for one metric tile */
  storeMetric(key: string, branchId?: number | null): Observable<any> {
    return this.http.post<any>(`${environment.chatbotApiUrl}store/metric`, {
      key, branchId: branchId == null ? null : branchId,
    });
  }

  /** Daily AI briefing over the store metrics */
  storeBriefing(branchId?: number | null): Observable<any> {
    return this.http.post<any>(`${environment.chatbotApiUrl}store/briefing`, {
      branchId: branchId == null ? null : branchId,
    });
  }

  /** Ask-your-store: natural-language question over the store data */
  storeAsk(question: string, branchId?: number | null): Observable<any> {
    return this.http.post<any>(`${environment.chatbotApiUrl}store/ask`, {
      question, branchId: branchId == null ? null : branchId,
    });
  }
}

export interface WorkupResponse {
  sections: any;
  counts: { [k: string]: number };
  model: string;
  ok: boolean;
  error?: string;
}

export interface TranscribeResponse {
  ok: boolean;
  transcript: string;
  language_code?: string;
  model?: string;
  error?: string;
}

export interface InventoryInsights {
  ok: boolean;
  model: string;
  stats: { totalItems: number; expiringSoon: number; belowReorder: number; deadStock: number; atRiskValue: number };
  savings: { writeOffAvoided: number; capitalToFree: number; salesProtected: number; totalImpact: number };
  summary: string;
  expiryRisk: any[];
  reorder: any[];
  deadStock: any[];
  source?: string;
  itemCount?: number;
}
